import { useState, useEffect, useCallback, useRef } from 'react';
import { n8nProxyService } from '@/services/n8nProxyService';
import { cacheService } from '@/services/cacheService';

export interface N8nStatus {
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  lastCheck: number;
  nextCheck: number;
  totalAttempts: number;
}

// Cache keys para status da integração n8n
const N8N_STATUS_CACHE_KEY = 'n8n:integrationStatus';
const N8N_CONNECTION_ATTEMPTS_KEY = 'n8n:connectionAttempts';

// Duração do cache adaptativo
const INITIAL_CACHE_DURATION = 15 * 1000; // 15 segundos inicialmente
const MAX_CACHE_DURATION = 2 * 60 * 1000; // 2 minutos no máximo
const CONNECTED_CACHE_DURATION = 10 * 60 * 1000; // 10 minutos quando conectado

/**
 * Hook para verificar se a integração com n8n está configurada e pronta
 * Implementa uma estratégia de cache adaptativo com verificações progressivas
 */
export function useN8nStatus() {
  const [status, setStatus] = useState<N8nStatus>({
    isReady: false,
    isLoading: true,
    error: null,
    lastCheck: 0,
    nextCheck: 0,
    totalAttempts: 0
  });
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const totalAttemptsRef = useRef<number>(cacheService.get<number>(N8N_CONNECTION_ATTEMPTS_KEY) || 0);

  // Função para calcular o tempo de espera entre verificações, aumentando gradualmente
  const getCheckInterval = useCallback(() => {
    const attempts = totalAttemptsRef.current;
    
    if (attempts <= 2) return INITIAL_CACHE_DURATION;
    if (attempts <= 5) return 30 * 1000; // 30 segundos
    if (attempts <= 10) return 60 * 1000; // 1 minuto
    return MAX_CACHE_DURATION; // 2 minutos
  }, []);

  // Função que determina se deve ignorar o cache com base no número de tentativas
  const shouldIgnoreCache = useCallback(() => {
    const attempts = totalAttemptsRef.current;
    
    // A cada 3 tentativas, ignorar cache completamente
    return attempts % 3 === 0;
  }, []);

  // Função principal para verificar o status
  const checkStatus = useCallback(async (forceRefresh = false) => {
    try {
      setStatus(prev => ({ 
        ...prev, 
        isLoading: true,
        lastCheck: Date.now()
      }));
      
      const ignoreCache = forceRefresh || shouldIgnoreCache();
      const attempts = totalAttemptsRef.current;
      
      console.log(`[useN8nStatus] Verificando conexão n8n (tentativa ${attempts + 1}, ignorando cache: ${ignoreCache})`);
      
      // Verificar se é instância compartilhada
      const config = await n8nProxyService.getN8nConfig();
      const isSharedInstance = !!config.workspace_id;
      
      if (isSharedInstance) {
        console.log(`[useN8nStatus] Usando instância compartilhada com workspace ${config.workspace_id}`);
      }
      
      // Testar a conexão com n8n
      const connectionResult = await n8nProxyService.testConnection(ignoreCache);
      
      // Incrementar contador de tentativas
      totalAttemptsRef.current += 1;
      cacheService.set(N8N_CONNECTION_ATTEMPTS_KEY, totalAttemptsRef.current);
      
      // Para instância compartilhada, reduzir o tempo de espera
      const nextInterval = connectionResult.connected ? 
        CONNECTED_CACHE_DURATION : 
        (isSharedInstance ? Math.min(getCheckInterval(), 30 * 1000) : getCheckInterval());
      
      const nextCheckTime = Date.now() + nextInterval;
      
      // Atualizar o status baseado na resposta
      const newStatus: N8nStatus = {
        isReady: connectionResult.connected,
        isLoading: false,
        error: connectionResult.error || null,
        lastCheck: Date.now(),
        nextCheck: nextCheckTime,
        totalAttempts: totalAttemptsRef.current
      };

      setStatus(newStatus);
      
      // Definir cache com duração adaptativa
      const cacheDuration = connectionResult.connected ? 
        CONNECTED_CACHE_DURATION : nextInterval;
        
      cacheService.set(N8N_STATUS_CACHE_KEY, newStatus, cacheDuration);
      
      // Agendar próxima verificação
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      timerRef.current = setTimeout(() => {
        checkStatus(false);
      }, nextInterval);
      
      return newStatus;
    } catch (error) {
      console.error('[useN8nStatus] Erro ao verificar status:', error);
      
      // Incrementar contador de tentativas mesmo em caso de erro
      totalAttemptsRef.current += 1;
      cacheService.set(N8N_CONNECTION_ATTEMPTS_KEY, totalAttemptsRef.current);
      
      // Calcular próximo intervalo
      const nextInterval = getCheckInterval();
      const nextCheckTime = Date.now() + nextInterval;
      
      // Atualizar status com erro
      const errorStatus: N8nStatus = {
        isReady: false,
        isLoading: false,
        error: error.message || 'Erro desconhecido ao verificar status',
        lastCheck: Date.now(),
        nextCheck: nextCheckTime,
        totalAttempts: totalAttemptsRef.current
      };
      
      setStatus(errorStatus);
      
      // Agendar próxima verificação
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      timerRef.current = setTimeout(() => {
        checkStatus(false);
      }, nextInterval);
      
      return errorStatus;
    }
  }, [shouldIgnoreCache, getCheckInterval]);
  
  // Inicializar o hook
  useEffect(() => {
    // Verificar se há status em cache
    const cachedStatus = cacheService.get<N8nStatus>(N8N_STATUS_CACHE_KEY);
    
    if (cachedStatus) {
      console.log('[useN8nStatus] Usando status em cache:', cachedStatus);
      
      // Verificar se o cache expirou
      if (Date.now() < cachedStatus.nextCheck) {
        setStatus(cachedStatus);
        
        // Agendar próxima verificação
        const timeUntilNextCheck = cachedStatus.nextCheck - Date.now();
        
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        
        timerRef.current = setTimeout(() => {
          checkStatus(false);
        }, timeUntilNextCheck);
      } else {
        // Cache expirado, verificar imediatamente
        checkStatus(false);
      }
    } else {
      // Sem cache, verificar imediatamente
      checkStatus(false);
    }
    
    // Limpar timer ao desmontar
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [checkStatus]);
  
  // Função para forçar uma verificação imediata
  const forceCheck = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    return checkStatus(true);
  }, [checkStatus]);
  
  // Função para calcular o tempo restante até a próxima verificação
  const getTimeUntilNextCheck = useCallback(() => {
    const now = Date.now();
    return Math.max(0, status.nextCheck - now);
  }, [status.nextCheck]);
  
  return {
    ...status,
    forceCheck,
    getTimeUntilNextCheck,
    checkStatus
  };
} 